import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useForm } from "antd/es/form/Form";
import dayjs from "dayjs";
import {
  Row,
  Col,
  Tag,
  Select,
  Button,
  Spin,
  Input,
  Empty,
  Pagination,
} from "antd";
import AdvertCard from "./Components/AdvertCard";
import Filter from "../Personnel/Filter/Filter";
import usePersonnelAdvertService from "../../services/usePersonnelAdvertService";
import { SORT_OPTIONS } from "../../utils/constantOptions";

const { Search } = Input;
const { Option } = Select;

const DEFAULT_SORT = "ADVERT_START_DATE_DESC";
const DEFAULT_PAGE_SIZE = 10;

const Listings = () => {
  const [form] = useForm();
  const location = useLocation();
  const { labelKey, searchQuery, cityId } = location.state || {};

  const [responseFilters, setResponseFilters] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [selectedCityId, setSelectedCityId] = useState(null);
  const [isInitialSearch, setIsInitialSearch] = useState(true);
  const [sortOption, setSortOption] = useState(DEFAULT_SORT);
  const [adverts, setAdverts] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    total: 0,
  });
  const [isLoading, setIsLoading] = useState(true);

  const { useFilterAdvert } = usePersonnelAdvertService();
  const { mutateAsync: filterAdvert, isPending: isAdvertsLoading } =
    useFilterAdvert();

  const fetchAdverts = async (
    page = 1,
    filters = {},
    sort = sortOption,
    searchQuery = searchText,
    labelKey,
    initialCityId = null
  ) => {
    const selectedSort = SORT_OPTIONS.find((option) => option.value === sort);
    const fields = {
      labelKey: labelKey,
      text: searchQuery,
      jobTypes: filters?.jobTypes || null,
      durationTypes: filters?.durationTypes || null,
      endDate: filters.endDate
        ? dayjs(filters?.endDate).format("YYYY-MM-DD")
        : null,
      startDate: filters.startDate
        ? dayjs(filters?.startDate).format("YYYY-MM-DD")
        : null,
      wagePeriods: filters?.wagePeriods || null,
      wageTypes: filters?.wageTypes || null,
      isExperienceRequired: filters.isExperienceRequired || null,
      minWage: filters?.minWage || null,
      maxWage: filters?.maxWage || null,
      countryIds: filters?.districtIds
        ? null
        : filters?.cityIds
        ? null
        : filters?.countryIds
        ? [filters.countryIds]
        : null,
      cityIds: filters?.districtIds
        ? null
        : isInitialSearch && initialCityId
        ? [initialCityId]
        : filters?.cityIds
        ? [filters.cityIds]
        : null,
      districtIds: filters?.districtIds ? [filters.districtIds] : null,
      optionIds: (() => {
        const ids = Object.values(filters.optionIds || {})
          .flat()
          .filter((id) => id != null);
        return ids.length ? ids : null;
      })(),
    };

    const requestBody = {
      fields,
      orderBy: selectedSort.orderBy,
      sortDirection: selectedSort.direction,
      pagination: {
        page: page - 1,
        size: pagination.pageSize,
      },
    };

    try {
      const response = await filterAdvert(requestBody);
      const { filters: responseFilters } = response.data;
      const { data, total, pageSize } = response.data.pagination;
      setResponseFilters(responseFilters);
      setSearchText(responseFilters.text);
      setAdverts(data);
      setPagination((prev) => ({
        ...prev,
        current: page,
        total,
        pageSize,
      }));

      if (isInitialSearch) {
        setIsInitialSearch(false);
      }
    } catch (error) {
      console.error("Error fetching adverts:", error);
    }
  };

  useEffect(() => {
    const initializePage = async () => {
      await fetchAdverts(1, DEFAULT_SORT);
      setIsLoading(false);
    };
    initializePage();
  }, []);

  const handleSearch = async (value) => {
    setSearchText(value);
    await fetchAdverts(1, form.getFieldsValue(), sortOption, value);
  };

  const handleSortChange = async (value) => {
    setSortOption(value);
    await fetchAdverts(1, form.getFieldsValue(), value, searchText);
  };

  const handlePageChange = async (page, pageSize) => {
    await fetchAdverts(page, form.getFieldsValue(), sortOption, searchText);
  };

  const handleFilterSubmit = async (values) => {
    await fetchAdverts(1, values, sortOption, searchText);
  };

  const clearFilters = async () => {
    form.resetFields();
    setSearchText("");
    setSelectedCityId(null);
    await fetchAdverts(1, {}, DEFAULT_SORT, "");
  };

  const getItemRangeText = () => {
    const start = (pagination.current - 1) * pagination.pageSize + 1;
    const end = Math.min(
      pagination.current * pagination.pageSize,
      pagination.total
    );
    return `${pagination.total} ilan arasından, ${start}-${end} arasındakiler gösteriliyor`;
  };

  if (isLoading) {
    return (
      <div style={styles.loadingContainer}>
        <Spin size="large" tip="Yükleniyor..." />
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.searchContainer}>
        <Search
          placeholder="İş ilanlarında ara..."
          enterButton="Ara"
          size="large"
          onSearch={handleSearch}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          loading={isAdvertsLoading}
        />
      </div>

      <div style={styles.header}>
        <h1 style={styles.headerTitle}>{pagination.total} İş İlanı</h1>
      </div>

      <Row gutter={[24, 24]}>
        <Col xs={24} sm={24} md={6} lg={6}>
          <Filter
            form={form}
            onFinish={handleFilterSubmit}
            responseFilters={responseFilters}
            selectedCityId={selectedCityId}
            setSelectedCityId={setSelectedCityId}
          />
        </Col>

        <Col xs={24} sm={24} md={18} lg={18}>
          <Spin spinning={isAdvertsLoading}>
            <div style={styles.activeFilters}>
              {/* <div style={styles.filterLeft}>
                <span style={styles.filterText}>Seçili Filtreler (1)</span>
                <Tag>Hibrit</Tag>
              </div> */}
              <Select
                value={sortOption}
                onChange={handleSortChange}
                style={{ width: 250 }}
                placeholder="Sıralama seçiniz">
                {SORT_OPTIONS.map((option) => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
              <Button style={styles.clearFiltersButton} onClick={clearFilters}>
                Filtreleri Temizle
              </Button>
            </div>

            <div style={styles.listingsGrid}>
              {adverts.length === 0 ? (
                <div style={styles.emptyContainer}>
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      <span style={{ fontSize: "16px", color: "#666" }}>
                        Aramanıza göre ilan bulamadık :(
                      </span>
                    }
                  />
                </div>
              ) : (
                <>
                  {adverts.map((advert) => (
                    <AdvertCard key={advert.id} advert={advert} />
                  ))}
                  <div style={styles.paginationWrapper}>
                    <div style={styles.itemRangeText}>{getItemRangeText()}</div>
                    <Pagination
                      current={pagination.current}
                      total={pagination.total}
                      pageSize={pagination.pageSize}
                      onChange={handlePageChange}
                      showSizeChanger={false}
                    />
                  </div>
                </>
              )}
            </div>
          </Spin>
        </Col>
      </Row>
    </div>
  );
};

export default Listings;

// Styles object
const styles = {
  container: {
    padding: "24px",
    maxWidth: "1400px",
    margin: "0 auto",
  },
  searchContainer: {
    marginBottom: "24px",
    maxWidth: "800px",
    marginLeft: "auto",
    marginRight: "auto",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "24px",
  },
  headerTitle: {
    margin: 0,
    fontSize: "24px",
    fontWeight: 600,
  },
  activeFilters: {
    marginBottom: "16px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  filterLeft: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  filterText: {
    color: "#666",
  },
  listingsGrid: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    minHeight: "200px",
  },
  paginationContainer: {
    textAlign: "center",
    marginTop: "24px",
    marginBottom: "48px",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh",
    backgroundColor: "rgba(255, 255, 255, 0.9)",
  },
  emptyContainer: {
    width: "100%",
    padding: "40px 0",
    textAlign: "center",
  },
  clearFiltersButton: {
    backgroundColor: "#1677ff",
    color: "white",
    border: "none",
    borderRadius: "6px",
    height: "32px",
    padding: "4px 15px",
  },
  itemRangeText: {
    color: "#666",
    fontSize: "14px",
    marginBottom: "12px",
  },
  paginationWrapper: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "12px",
    marginTop: "24px",
    marginBottom: "48px",
    borderTop: "1px solid #f0f0f0",
    paddingTop: "24px",
  },
};
